"use client";

import { ApiReferenceReact } from "@scalar/api-reference-react";
import "@scalar/api-reference-react/style.css";
import { ArrowLeft, Code2, Moon, Sun } from "lucide-react";
import Link from "next/link";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { LiquidAmbientBackground } from "@/components/ui/LiquidGlass";

export default function DevelopersPage() {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const isDark = mounted ? resolvedTheme === "dark" : true; // Default dark before mount to avoid flash

  return (
    <div className={`min-h-screen flex flex-col font-sans transition-colors duration-300 relative overflow-hidden ${isDark ? 'bg-[#060a12] text-white' : 'bg-[#FAFAFA] text-gray-900'}`}>
      <LiquidAmbientBackground intensity="default" />
      {/* Premium Header */}
      <header className="sticky top-0 z-[100] liquid-glass-elevated border-b border-white/15 px-6 py-4 transition-colors">
        <div className="max-w-[1920px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link 
              href="/" 
              className={`group flex items-center gap-2 transition-colors ${isDark ? 'text-white/70 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
            >
              <div className="liquid-glass-button p-2 rounded-full transition-colors">
                <ArrowLeft className="w-4 h-4" />
              </div>
              <span className="text-sm font-medium tracking-wide">Back to Hub</span>
            </Link>
            
            <div className={`h-6 w-px ${isDark ? 'bg-white/10' : 'bg-black/10'}`} />
            
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl liquid-glass-tint">
                <Code2 className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <h1 className="text-sm font-semibold tracking-wide">YoHub Developers</h1>
                <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>API & Webhooks Reference</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {mounted && (
              <button 
                onClick={() => setTheme(isDark ? 'light' : 'dark')}
                className="liquid-glass-button p-2 rounded-full transition-colors text-white/80 hover:text-white"
                aria-label="Toggle theme"
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
            )}
            <div className="flex px-4 py-1.5 rounded-full liquid-glass">
              <span className="text-xs font-medium text-indigo-400">v1.0.0 Stable</span>
            </div>
          </div>
        </div>
      </header>

      {/* Scalar Container */}
      <main className="flex-grow w-full scalar-container relative z-10">
        <div className={`absolute inset-0 bg-[size:24px_24px] ${isDark ? 'bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)]' : 'bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)]'}`} />
        
        <div className="relative z-10 w-full h-[calc(100vh-76px)] overflow-auto">
          {mounted && (
            <ApiReferenceReact
              configuration={{
                spec: {
                  url: "/openapi.yaml",
                },
                theme: "purple", // "purple", "moon", "default"
                darkMode: isDark,
                layout: "modern",
                showSidebar: true,
                metaData: {
                  title: "YoHub API Documentation",
                  description: "Technical & functional documentation for YoHub APIs",
                },
              } as any}
            />
          )}
        </div>
      </main>
      
      {/* Custom CSS for Scalar overrides to match premium look in both modes */}
      <style dangerouslySetInnerHTML={{ __html: `
        .scalar-container {
           --theme-background-1: transparent;
           --theme-background-2: ${isDark ? 'rgba(15, 23, 42, 0.55)' : '#FFFFFF'};
           --theme-background-3: ${isDark ? 'rgba(255, 255, 255, 0.06)' : '#F1F5F9'};
           --theme-color-1: ${isDark ? '#FFFFFF' : '#111827'};
           --theme-color-2: ${isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(17, 24, 39, 0.7)'};
           --theme-color-3: ${isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(17, 24, 39, 0.5)'};
           --theme-color-accent: ${isDark ? '#818cf8' : '#6366f1'}; /* indigo-400 / indigo-500 */
        }
        
        .scalar-app {
           font-family: inherit !important;
           background: transparent !important;
        }
      `}} />
    </div>
  );
}
